// Builds and caches every kit paradigm. The book tables (conjugateBook, kitBookVerbs) match data/*.json exactly
// (tests/grammar/engine.test.ts A1); the tables the app uses add every विकल्प of the notes on top (withVikalp).

import type {
    Gender,
    NounEntry,
    RootClass,
    VerbEntry
} from '@/prakrit/lib/grammar/types'

import { buildKitNouns, buildNoun } from '@/prakrit/lib/grammar/nouns'
import { conjA, conjLong } from '@/prakrit/lib/grammar/verbs'
import { withVikalp } from '@/prakrit/lib/grammar/pratyaya'
import { SOURCE, VERBS_A, VERBS_LONG } from '@/prakrit/lib/grammar/lexicon'

let verbCache: VerbEntry[] | null = null
let bookVerbCache: VerbEntry[] | null = null
let nounCache: NounEntry[] | null = null
let verbIndex: Map<string, VerbEntry> | null = null
let nounIndex: Map<string, NounEntry> | null = null

/** Root class from the written root: आ / ए / ओ-ending roots conjugate differently. */
export const rootClassOf = (root: string): RootClass => /[ाेो]$|[आएओ]$/.test(root) ? 'long' : 'a'

/** The course book's own table (conjA / conjLong), exactly as data/verbs.json. */
export const conjugateBook = (root: string, rootClass: RootClass = rootClassOf(root)): VerbEntry['forms'] => (
    rootClass === 'long' ? conjLong(root) : conjA(root)
)

/** The table the app uses: the book's forms plus every विकल्प of the notes (pratyaya.ts), added as 'alt'. */
export const conjugate = (root: string, rootClass: RootClass = rootClassOf(root)): VerbEntry['forms'] => (
    withVikalp(root, rootClass, conjugateBook(root, rootClass))
)

export const makeVerb = (root: string, hi: string, en: string, rootClass: RootClass = rootClassOf(root), source = SOURCE): VerbEntry => ({
    root,
    class: rootClass,
    hi,
    en,
    source,
    forms: conjugate(root, rootClass)
})

export const makeNoun = (stem: string, gender: Gender, hi: string, en: string): NounEntry => buildNoun(stem, gender, hi, en)

/** All 103 kit verbs, in data/verbs.json order. */
export const kitVerbs = (): VerbEntry[] => {
    if (!verbCache) {
        verbCache = [
            ...VERBS_A.map(([s, hi, en]) => makeVerb(s, hi, en, 'a')),
            ...VERBS_LONG.map(([s, hi, en]) => makeVerb(s, hi, en, 'long'))
        ]
    }

    return verbCache
}

/** All 103 kit verbs with the book's tables only (no notes विकल्प): what data/verbs.json holds. */
export const kitBookVerbs = (): VerbEntry[] => {
    if (!bookVerbCache) {
        bookVerbCache = [
            ...VERBS_A.map(([s, hi, en]): VerbEntry => ({ root: s, class: 'a', hi, en, source: SOURCE, forms: conjugateBook(s, 'a') })),
            ...VERBS_LONG.map(([s, hi, en]): VerbEntry => ({ root: s, class: 'long', hi, en, source: SOURCE, forms: conjugateBook(s, 'long') }))
        ]
    }

    return bookVerbCache
}

/** All kit nouns, in data/nouns.json order. */
export const kitNouns = (): NounEntry[] => {
    if (!nounCache) nounCache = buildKitNouns()

    return nounCache
}

export const getVerb = (root: string): VerbEntry | undefined => {
    if (!verbIndex) verbIndex = new Map(kitVerbs().map(v => [v.root, v]))

    return verbIndex.get(root.normalize('NFC'))
}

export const getNoun = (stem: string): NounEntry | undefined => {
    if (!nounIndex) nounIndex = new Map(kitNouns().map(n => [n.stem, n]))

    return nounIndex.get(stem.normalize('NFC'))
}
